import { CAD_KINDS } from '../data/cadConstants';

const ARG_LNG_MIN = -75;
const ARG_LNG_MAX = -50;
const ARG_LAT_MIN = -56;
const ARG_LAT_MAX = -21;

export const CAD_DISPLAY_MODES = ['map', 'canvas', 'image'];

/**
 * Primer [lng, lat] de un GeoJSON Geometry.
 * @param {object|null} geometry
 * @returns {[number, number]|null}
 */
export function firstCoordinate(geometry) {
  if (!geometry) return null;
  const walk = (node) => {
    if (!Array.isArray(node) || node.length === 0) return null;
    if (typeof node[0] === 'number' && typeof node[1] === 'number') {
      return [node[0], node[1]];
    }
    return walk(node[0]);
  };
  return walk(geometry.coordinates);
}

/**
 * Heurística: `geom_4326` es lng/lat. `geom` del DXF son metros.
 * Si el primer vértice cae en el bbox de Argentina, se puede pintar en el mapa.
 * @param {object|null} geometry
 */
export function isLikelyWgs84(geometry) {
  const coord = firstCoordinate(geometry);
  if (!coord) return false;
  const [lng, lat] = coord;
  return lng >= ARG_LNG_MIN && lng <= ARG_LNG_MAX
    && lat >= ARG_LAT_MIN && lat <= ARG_LAT_MAX;
}

/**
 * Lienzo que eligió el dueño. El GET ya manda el GeoJSON de esa columna
 * (`geom_4326` o `geom`). Sin `display_mode`, srid 4326 → mapa.
 *
 * @param {object|null} project
 * @returns {'map'|'canvas'|'image'}
 */
export function getCadDisplayMode(project) {
  const mode = project?.display_mode;
  if (CAD_DISPLAY_MODES.includes(mode)) return mode;
  if (Number(project?.srid) === 4326 || project?.geometry_source === 'geom_4326') {
    return 'map';
  }
  return 'canvas';
}

export function isCadMapDisplay(project) {
  return getCadDisplayMode(project) === 'map';
}

/** Resumen de listado: se puede ubicar en el mapa país si el lienzo es `map`. */
export function canLocateCadProjectOnMap(project) {
  return Boolean(project?.slug) && isCadMapDisplay(project);
}

/**
 * `canvas` e `image` pintan el plano en metros. `image` sin archivo se
 * trata como `canvas` (fondo neutro).
 */
export function isCadPlanDisplay(project) {
  const mode = getCadDisplayMode(project);
  return mode === 'canvas' || mode === 'image';
}

/**
 * Hay geometría usable en el mapa solo si los vértices son WGS84 (lng/lat).
 * Un zip viejo con eastings no va a MapLibre aunque el envelope diga 4326.
 *
 * @param {object|null} project  data de GET /getcadproject
 */
export function canPaintCadProjectOnMap(project) {
  const features = project?.parcels?.features;
  if (!Array.isArray(features) || features.length === 0) return false;
  const withGeom = features.filter((feature) => feature?.geometry);
  if (withGeom.length === 0) return false;
  return withGeom.every((feature) => isLikelyWgs84(feature.geometry));
}

/** Overlay MapLibre: modo mapa y vértices lng/lat. No es un error si da false. */
export function shouldPaintCadOnMap(project) {
  return isCadMapDisplay(project) && canPaintCadProjectOnMap(project);
}

/**
 * `bounds` del GET: `[[minX, minY], [maxX, maxY]]`.
 * @param {unknown} bounds
 * @returns {{ minX: number, minY: number, maxX: number, maxY: number }|null}
 */
export function parseCadBounds(bounds) {
  if (!Array.isArray(bounds) || bounds.length < 2) return null;
  const min = bounds[0];
  const max = bounds[1];
  if (!Array.isArray(min) || !Array.isArray(max) || min.length < 2 || max.length < 2) {
    return null;
  }
  const minX = Number(min[0]);
  const minY = Number(min[1]);
  const maxX = Number(max[0]);
  const maxY = Number(max[1]);
  if (![minX, minY, maxX, maxY].every(Number.isFinite)) return null;
  if (maxX === minX && maxY === minY) return null;
  return { minX, minY, maxX, maxY };
}

/**
 * @param {object|null} featureCollection
 * @returns {[number, number, number, number]|null}
 */
export function computeFeatureCollectionBbox(featureCollection) {
  const features = featureCollection?.features;
  if (!Array.isArray(features) || features.length === 0) return null;

  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  let found = false;

  features.forEach((feature) => {
    const bbox = computeGeometryBbox(feature?.geometry);
    if (!bbox) return;
    found = true;
    if (bbox[0] < minLng) minLng = bbox[0];
    if (bbox[1] < minLat) minLat = bbox[1];
    if (bbox[2] > maxLng) maxLng = bbox[2];
    if (bbox[3] > maxLat) maxLat = bbox[3];
  });

  return found ? [minLng, minLat, maxLng, maxLat] : null;
}

/**
 * Une bboxes `[minLng, minLat, maxLng, maxLat]`.
 * @param {Array<[number, number, number, number]|null|undefined>} bboxes
 * @returns {[number, number, number, number]|null}
 */
export function unionLngLatBboxes(bboxes) {
  if (!Array.isArray(bboxes) || bboxes.length === 0) return null;
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  let found = false;
  bboxes.forEach((bbox) => {
    if (!Array.isArray(bbox) || bbox.length < 4) return;
    const west = Number(bbox[0]);
    const south = Number(bbox[1]);
    const east = Number(bbox[2]);
    const north = Number(bbox[3]);
    if (![west, south, east, north].every(Number.isFinite)) return;
    found = true;
    if (west < minLng) minLng = west;
    if (south < minLat) minLat = south;
    if (east > maxLng) maxLng = east;
    if (north > maxLat) maxLat = north;
  });
  return found ? [minLng, minLat, maxLng, maxLat] : null;
}

/**
 * True si `next` cubre más terreno que `prev` (p. ej. más teselas del mismo lote).
 * @param {[number, number, number, number]|null|undefined} next
 * @param {[number, number, number, number]|null|undefined} prev
 * @param {number} [eps]
 * @returns {boolean}
 */
export function isLngLatBboxMoreComplete(next, prev, eps = 1e-7) {
  if (!Array.isArray(next) || next.length < 4) return false;
  if (!Array.isArray(prev) || prev.length < 4) return true;
  const expands = next[0] < prev[0] - eps
    || next[1] < prev[1] - eps
    || next[2] > prev[2] + eps
    || next[3] > prev[3] + eps;
  if (!expands) return false;
  const nextArea = Math.max(0, next[2] - next[0]) * Math.max(0, next[3] - next[1]);
  const prevArea = Math.max(0, prev[2] - prev[0]) * Math.max(0, prev[3] - prev[1]);
  return nextArea > prevArea * 1.005;
}

/**
 * Encaje del plano: `bounds` del API, o bbox del GeoJSON.
 * @param {object|null} project
 * @param {object|null} [featureCollection]
 */
export function readCadPlanBounds(project, featureCollection) {
  const fromApi = parseCadBounds(project?.bounds);
  if (fromApi) return fromApi;
  const bbox = computeFeatureCollectionBbox(featureCollection || project?.parcels);
  if (!bbox) return null;
  return { minX: bbox[0], minY: bbox[1], maxX: bbox[2], maxY: bbox[3] };
}

/**
 * Escala local metros → grados cerca del ecuador.
 * Distorsión despreciable en un loteo de unos cientos de metros.
 */
export const CAD_PLAN_METERS_PER_DEGREE = 111320;

/**
 * Origen del plano (centro del bbox en metros DXF).
 * @param {{ minX: number, minY: number, maxX: number, maxY: number }|null} bounds
 * @returns {{ originX: number, originY: number }|null}
 */
export function cadPlanOrigin(bounds) {
  if (!bounds) return null;
  return {
    originX: (bounds.minX + bounds.maxX) / 2,
    originY: (bounds.minY + bounds.maxY) / 2,
  };
}

/**
 * Metro DXF → lng/lat sintético del plano (mismo origen que el GeoJSON).
 * @param {number} x
 * @param {number} y
 * @param {{ minX: number, minY: number, maxX: number, maxY: number }|null} bounds
 * @returns {[number, number]|null}
 */
export function projectCadPlanPoint(x, y, bounds) {
  const origin = cadPlanOrigin(bounds);
  const nx = Number(x);
  const ny = Number(y);
  if (!origin || !Number.isFinite(nx) || !Number.isFinite(ny)) return null;
  return [
    (nx - origin.originX) / CAD_PLAN_METERS_PER_DEGREE,
    (ny - origin.originY) / CAD_PLAN_METERS_PER_DEGREE,
  ];
}

/**
 * Esquinas en metros → lng/lat sintético del plano (NW, NE, SE, SW).
 * @param {Array<[number, number]>|null} corners
 * @param {{ minX: number, minY: number, maxX: number, maxY: number }|null} bounds
 * @returns {[[number, number], [number, number], [number, number], [number, number]]|null}
 */
export function projectCadPlanCorners(corners, bounds) {
  if (!Array.isArray(corners) || corners.length < 4 || !bounds) return null;
  const projected = corners.slice(0, 4).map((pair) => {
    if (!Array.isArray(pair) || pair.length < 2) return null;
    return projectCadPlanPoint(pair[0], pair[1], bounds);
  });
  return projected.every(Boolean) ? projected : null;
}

/**
 * Esquinas del PNG para `image` source de MapLibre (NW, NE, SE, SW).
 * Por defecto llena `bounds`. El contain + ajuste van en `cadCanvasImage`.
 * @param {{ minX: number, minY: number, maxX: number, maxY: number }|null} bounds
 * @returns {[[number, number], [number, number], [number, number], [number, number]]|null}
 */
export function cadPlanImageCoordinates(bounds) {
  if (!bounds) return null;
  return projectCadPlanCorners([
    [bounds.minX, bounds.maxY],
    [bounds.maxX, bounds.maxY],
    [bounds.maxX, bounds.minY],
    [bounds.minX, bounds.minY],
  ], bounds);
}

/**
 * Click del plano (lng/lat sintéticos) → metros DXF (`georef_points.src`).
 * @param {number} lng
 * @param {number} lat
 * @param {{ minX: number, minY: number, maxX: number, maxY: number }|null} bounds
 * @returns {[number, number]|null}
 */
export function unprojectCadPlanLngLat(lng, lat, bounds) {
  const origin = cadPlanOrigin(bounds);
  const nx = Number(lng);
  const ny = Number(lat);
  if (!origin || !Number.isFinite(nx) || !Number.isFinite(ny)) return null;
  return [
    nx * CAD_PLAN_METERS_PER_DEGREE + origin.originX,
    ny * CAD_PLAN_METERS_PER_DEGREE + origin.originY,
  ];
}

function mapCadPositions(node, projectPoint) {
  if (!Array.isArray(node) || node.length === 0) return node;
  if (typeof node[0] === 'number' && typeof node[1] === 'number') {
    const [lng, lat] = projectPoint(node[0], node[1]);
    if (node.length === 2) return [lng, lat];
    return [lng, lat, ...node.slice(2)];
  }
  return node.map((child) => mapCadPositions(child, projectPoint));
}

/**
 * Aplica `projectPoint(x, y) → [lng, lat]` a cada vértice.
 * @param {object|null} featureCollection
 * @param {(x: number, y: number) => [number, number]} projectPoint
 */
export function mapCadFeatureCollection(featureCollection, projectPoint) {
  const normalized = normalizeCadFeatureCollection(featureCollection);
  return {
    type: 'FeatureCollection',
    features: normalized.features.map((feature) => {
      if (!feature?.geometry?.coordinates) return feature;
      return {
        ...feature,
        geometry: {
          ...feature.geometry,
          coordinates: mapCadPositions(feature.geometry.coordinates, projectPoint),
        },
      };
    }),
  };
}

/**
 * MapLibre no acepta metros ni eastings en el mapa país.
 * El plano (`canvas` / `image`) usa un MapLibre sin teselas: se centra el
 * loteo en (0, 0) y se escala a grados para pan / zoom / pitch / tooltip.
 *
 * @param {object|null} featureCollection
 * @param {{ minX: number, minY: number, maxX: number, maxY: number }|null} [bounds]
 */
export function projectCadPlanCollection(featureCollection, bounds) {
  const normalized = normalizeCadFeatureCollection(featureCollection);
  const box = bounds || readCadPlanBounds(null, normalized);
  if (!box) return normalized;
  const origin = cadPlanOrigin(box);
  const originX = origin.originX;
  const originY = origin.originY;
  const projectPoint = (x, y) => {
    const nx = Number(x);
    const ny = Number(y);
    if (!Number.isFinite(nx) || !Number.isFinite(ny)) return [x, y];
    return [
      (nx - originX) / CAD_PLAN_METERS_PER_DEGREE,
      (ny - originY) / CAD_PLAN_METERS_PER_DEGREE,
    ];
  };
  return mapCadFeatureCollection(normalized, projectPoint);
}

/**
 * Halo amplio alrededor del plano: se puede panear y alejar, no irse al infinito.
 * El ratio es por lado, usando el lado más largo para no apretar un loteo chato.
 */
export const CAD_PLAN_BOUNDS_PAD_RATIO = 8;

/**
 * @param {[number, number, number, number]|null} bbox minLng, minLat, maxLng, maxLat
 * @param {number} [paddingRatio]
 * @returns {[number, number, number, number]|null}
 */
export function expandCadPlanBbox(bbox, paddingRatio = CAD_PLAN_BOUNDS_PAD_RATIO) {
  if (!Array.isArray(bbox) || bbox.length < 4) return null;
  const minLng = Number(bbox[0]);
  const minLat = Number(bbox[1]);
  const maxLng = Number(bbox[2]);
  const maxLat = Number(bbox[3]);
  if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return null;
  const spanX = Math.max(maxLng - minLng, 0);
  const spanY = Math.max(maxLat - minLat, 0);
  const pad = Math.max(spanX, spanY, 1 / CAD_PLAN_METERS_PER_DEGREE) * paddingRatio;
  return [minLng - pad, minLat - pad, maxLng + pad, maxLat + pad];
}

/**
 * @param {object|null} geometry
 * @returns {[number, number, number, number]|null} minLng, minLat, maxLng, maxLat
 */
export function computeGeometryBbox(geometry) {
  if (!geometry) return null;
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  let found = false;

  const visit = (node) => {
    if (!Array.isArray(node) || node.length === 0) return;
    if (typeof node[0] === 'number' && typeof node[1] === 'number') {
      const lng = node[0];
      const lat = node[1];
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
      found = true;
      if (lng < minLng) minLng = lng;
      if (lat < minLat) minLat = lat;
      if (lng > maxLng) maxLng = lng;
      if (lat > maxLat) maxLat = lat;
      return;
    }
    node.forEach(visit);
  };

  visit(geometry.coordinates);
  if (!found) return null;
  return [minLng, minLat, maxLng, maxLat];
}

/**
 * Copia `Feature.id` a `properties.id` para MapLibre `promoteId`.
 * @param {object|null} featureCollection
 */
export function normalizeCadFeatureCollection(featureCollection) {
  const features = Array.isArray(featureCollection?.features)
    ? featureCollection.features
    : [];
  return {
    type: 'FeatureCollection',
    features: features.map((feature) => {
      const id = feature?.id ?? feature?.properties?.id ?? null;
      return {
        ...feature,
        id,
        properties: {
          ...(feature?.properties || {}),
          id,
        },
      };
    }),
  };
}

/**
 * @param {object} project
 * @param {string} parcelId
 * @param {object} patch
 */
export function applyCadParcelPatch(project, parcelId, patch) {
  if (!project?.parcels) return project;
  const id = String(parcelId);
  const features = project.parcels.features.map((feature) => {
    const featureId = String(feature?.id ?? feature?.properties?.id ?? '');
    if (featureId !== id) return feature;
    const previous = feature.properties || {};
    const nextProps = {
      ...previous,
      ...patch,
      id: previous.id ?? feature.id,
    };
    if (Object.prototype.hasOwnProperty.call(patch || {}, 'cad_kind')) {
      const kind = String(patch.cad_kind || '').trim().toLowerCase();
      if (CAD_KINDS.includes(kind)) {
        nextProps.cad_kind = kind;
        nextProps.attributes = {
          ...(previous.attributes && typeof previous.attributes === 'object'
            ? previous.attributes
            : {}),
          cad_kind: kind,
        };
        // Si el PATCH no manda inventario, el API pone amenity + unavailable.
        if (kind !== 'lot') {
          if (!Object.prototype.hasOwnProperty.call(patch, 'stock_status')) {
            nextProps.stock_status = 'unavailable';
          }
          if (!Object.prototype.hasOwnProperty.call(patch, 'use')) {
            nextProps.use = 'amenity';
          }
        }
      }
    }
    return {
      ...feature,
      properties: nextProps,
    };
  });
  return {
    ...project,
    parcels: { ...project.parcels, features },
  };
}

/**
 * Saca una parcela del FeatureCollection local (después de DELETE).
 * @param {object} project
 * @param {string} parcelId
 */
export function removeCadParcelFromProject(project, parcelId) {
  if (!project?.parcels) return project;
  const id = String(parcelId);
  const features = (project.parcels.features || []).filter((feature) => {
    const featureId = String(feature?.id ?? feature?.properties?.id ?? '');
    return featureId !== id;
  });
  return {
    ...project,
    parcels: { ...project.parcels, features },
  };
}

function pickCadExteriorRing(geometry) {
  const type = geometry?.type;
  const coords = geometry?.coordinates;
  if (!Array.isArray(coords) || coords.length === 0) return null;
  if (type === 'Polygon') return coords[0];
  if (type === 'MultiPolygon') return coords[0]?.[0] || null;
  if (type === 'LineString') return coords;
  return null;
}

/**
 * Convierte la geometría CAD al `boundary` `{ lat, lng }[]` que espera
 * `PlanimetriaSection`. No toca el DockPanel: WGS84 va directo; metros del
 * DXF se expresan como lat/lng sintéticos (escala `CAD_PLAN_METERS_PER_DEGREE`)
 * para que las cotas geodésicas redondeen a metros.
 *
 * @param {object|null} feature
 * @returns {{ lat: number, lng: number }[]}
 */
export function cadFeatureToPlanimetriaBoundary(feature) {
  const ring = pickCadExteriorRing(feature?.geometry);
  if (!Array.isArray(ring) || ring.length < 3) return [];
  const wgs84 = isLikelyWgs84(feature.geometry);
  const points = [];
  ring.forEach((position) => {
    if (!Array.isArray(position) || position.length < 2) return;
    const x = Number(position[0]);
    const y = Number(position[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    if (wgs84) {
      points.push({ lat: y, lng: x });
      return;
    }
    points.push({
      lat: y / CAD_PLAN_METERS_PER_DEGREE,
      lng: x / CAD_PLAN_METERS_PER_DEGREE,
    });
  });
  if (points.length >= 2) {
    const first = points[0];
    const last = points[points.length - 1];
    if (first.lat === last.lat && first.lng === last.lng) points.pop();
  }
  return points.length >= 3 ? points : [];
}
